import styled from 'styled-components';
import React, { useContext, useMemo } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import { SummaryContext } from '@/features/details/summary/context';
import { useMetricName } from '@/features/details/summary/summary-header';
import { naturalSorting } from '@/utils/experience-utils';
import {
  SummaryBody,
  SummaryValue,
} from '@/features/details/summary/summary-value';
import { HSpace } from '@/components/spacing';
import { ConfidenceIntervals } from '@/features/details/summary/confidence-interval';
import { getUplift } from '@/features/details/summary/util';
import { getFormattedChanceToWin } from '@/features/details/summary/index';

export function MobileSummaryTable({ ciEnabled }: { ciEnabled?: boolean }) {
  const {
    moreMetrics,
    variants,
    hasOneVariant,
    primaryMetrics,
    data,
    primaryGoal,
  } = useContext(SummaryContext);
  const [audience] = useQueryParam(`aud`);
  const [sessionType] = useQueryParam<string>(`st`, StringParam);
  const allMetrics = useMemo(
    () => [...primaryMetrics, ...moreMetrics],
    [primaryMetrics, moreMetrics],
  );

  if (variants?.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <List>
        <HeaderRow>Variation</HeaderRow>
        {allMetrics.map((metric) => (
          <HeaderRow key={metric}>
            {
              useMetricName(metric, false, audience as string, sessionType)
                ?.title
            }
          </HeaderRow>
        ))}
        {!hasOneVariant && (
          <>
            <HeaderRow>OTW</HeaderRow>
            {ciEnabled && <HeaderRow>CI</HeaderRow>}
          </>
        )}
      </List>
      <Scroller>
        {variants?.sort(naturalSorting).map((variant) => (
          <Card className="summary-card" key={variant.id}>
            <HeaderRow className="bold">{variant.name}</HeaderRow>
            {allMetrics.map((metric) => (
              <SummaryValue key={metric} metric={metric} variant={variant} />
            ))}
            {!hasOneVariant && (
              <SummaryBody>
                <span>
                  {getFormattedChanceToWin({
                    data,
                    v: variant,
                    primaryGoal,
                    variants,
                  })}
                </span>
              </SummaryBody>
            )}
            {ciEnabled && !hasOneVariant && (
              <ConfidenceIntervals
                variant={variant}
                uplift={getUplift(data, primaryGoal, variants, variant.id)}
              />
            )}
          </Card>
        ))}
        {!hasOneVariant && <HSpace value={2} />}
      </Scroller>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;

  && > *:first-child {
    flex: 0 0 auto;
  }
  padding-bottom: 1rem;
  padding-right: 1rem;
`;

const Scroller = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  gap: 1.5rem;

  width: 100%;
  padding: 10px;
  margin: -10px;

  mask-image: linear-gradient(
    var(--mask-direction, to right),
    hsl(0 0% 0% / 0),
    rgb(248 249 251 / 1) 5%,
    rgb(248 249 251 / 1) 95%,
    hsl(0 0% 0% / 0)
  );

  && > * {
    flex: 1 0 auto;
  }

  &&:has(.ci-graph) > .summary-card {
    min-width: 165px;
  }
`;

const List = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;

  && > * {
    min-height: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const Card = styled(List)`
  && {
    background: white;
    border-radius: 0.8rem;
    justify-content: center;
    align-items: center;
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.15),
      0 4px 11px 0 rgba(151, 210, 206, 0.22);
  }
`;

const HeaderRow = styled.span`
  text-align: start;
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  color: #a6afb8;

  &&.bold {
    font-weight: 700;
    color: black;
  }
`;
